import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import logger from './logger';

// Produits de démonstration pour initialiser la boutique
const produitsDemo = [
  {
    nom: 'Maillot Domicile EFAFC 2024',
    description: 'Maillot officiel domicile avec les nouvelles couleurs du club. Technologie anti-transpiration et coupe moderne pour un confort optimal.',
    prix: 65,
    prixOriginal: 75,
    categorie: 'maillot',
    tailles: ['S', 'M', 'L', 'XL', 'XXL'],
    stock: 15,
    images: [
      '/api/placeholder/400/400',
      '/api/placeholder/400/401'
    ],
    promotion: 13,
    actif: true,
    rating: 4.8,
    reviews: 24,
    nouveaute: true,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  },
  {
    nom: 'Maillot Extérieur EFAFC 2024',
    description: 'Maillot extérieur élégant avec design moderne. Parfait pour représenter le club lors des déplacements.',
    prix: 65,
    categorie: 'maillot',
    tailles: ['S', 'M', 'L', 'XL', 'XXL'],
    stock: 8,
    images: [
      '/api/placeholder/400/402',
      '/api/placeholder/400/403'
    ],
    actif: true,
    rating: 4.6,
    reviews: 18,
    nouveaute: false,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  },
  {
    nom: 'Écharpe Officielle EFAFC',
    description: 'Écharpe officielle aux couleurs du club. Idéale pour les matchs et montrer votre soutien inconditionnel.',
    prix: 25,
    categorie: 'supporter',
    stock: 30,
    images: [
      '/api/placeholder/400/404'
    ],
    actif: true,
    rating: 4.9,
    reviews: 45,
    nouveaute: false,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  },
  {
    nom: 'Casquette EFAFC Premium',
    description: 'Casquette ajustable avec logo brodé de qualité supérieure. Protection solaire et style garantis.',
    prix: 22,
    categorie: 'accessoire',
    stock: 20,
    images: [
      '/api/placeholder/400/405'
    ],
    actif: true,
    rating: 4.4,
    reviews: 12,
    nouveaute: false,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  },
  {
    nom: 'Short de Training EFAFC',
    description: 'Short d\'entraînement professionnel. Tissu respirant et coupe moderne pour les performances optimales.',
    prix: 35,
    categorie: 'maillot',
    tailles: ['S', 'M', 'L', 'XL'],
    stock: 12,
    images: [
      '/api/placeholder/400/406'
    ],
    actif: true,
    rating: 4.7,
    reviews: 8,
    nouveaute: false,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  },
  {
    nom: 'Gourde Isotherme EFAFC',
    description: 'Gourde isotherme aux couleurs du club. Capacité 500ml, garde vos boissons à température idéale.',
    prix: 18,
    categorie: 'accessoire',
    stock: 25,
    images: [
      '/api/placeholder/400/407'
    ],
    actif: true,
    rating: 4.3,
    reviews: 15,
    nouveaute: false,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  },
  {
    nom: 'Survêtement EFAFC Complet',
    description: 'Ensemble survêtement officiel veste + pantalon. Idéal pour l\'échauffement et la détente.',
    prix: 89,
    prixOriginal: 110,
    categorie: 'maillot',
    tailles: ['S', 'M', 'L', 'XL', 'XXL'],
    stock: 6,
    images: [
      '/api/placeholder/400/408',
      '/api/placeholder/400/409'
    ],
    promotion: 19,
    actif: true,
    rating: 4.5,
    reviews: 9,
    nouveaute: true,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  },
  {
    nom: 'Sac de Sport EFAFC',
    description: 'Sac de sport spacieux avec compartiments multiples. Logo EFAFC brodé et matériau résistant.',
    prix: 45,
    categorie: 'accessoire',
    stock: 18,
    images: [
      '/api/placeholder/400/410'
    ],
    actif: true,
    rating: 4.2,
    reviews: 7,
    nouveaute: false,
    ventes: 0,
    dateCreation: new Date(),
    dateModification: new Date()
  }
];

// Fonction pour vérifier si la boutique est déjà initialisée
export const checkIfBoutiqueInitialized = async () => {
  try {
    const q = query(collection(db, 'boutique_produits'));
    const snapshot = await getDocs(q);
    return snapshot.size > 0;
  } catch (error) {
    logger.error('Erreur lors de la vérification:', error);
    return false;
  }
};

// Fonction pour initialiser la boutique avec les produits de démonstration
export const initializeBoutique = async () => {
  try {
    logger.log('🛍️ Initialisation de la boutique...');
    
    // Vérifier si déjà initialisée
    const isInitialized = await checkIfBoutiqueInitialized();
    if (isInitialized) {
      logger.log('✅ La boutique est déjà initialisée');
      return { success: true, message: 'Boutique déjà initialisée' };
    }

    // Ajouter tous les produits de démonstration
    const addPromises = produitsDemo.map(produit => 
      addDoc(collection(db, 'boutique_produits'), produit)
    );
    
    await Promise.all(addPromises);
    
    logger.log('✅ Boutique initialisée avec succès!');
    return { 
      success: true, 
      message: `${produitsDemo.length} produits ajoutés avec succès!` 
    };
    
  } catch (error) {
    logger.error('❌ Erreur lors de l\'initialisation:', error);
    return { 
      success: false, 
      message: 'Erreur lors de l\'initialisation: ' + error.message 
    };
  }
};

// Fonction pour ajouter un produit de démonstration spécifique
export const addDemoProduct = async (productData) => {
  try {
    const produitData = {
      ...productData,
      dateCreation: new Date(),
      dateModification: new Date(),
      ventes: 0,
      actif: true
    };
    
    const docRef = await addDoc(collection(db, 'boutique_produits'), produitData);
    logger.log('✅ Produit ajouté:', docRef.id);
    return { success: true, id: docRef.id };
    
  } catch (error) {
    logger.error('❌ Erreur ajout produit:', error);
    return { success: false, message: error.message };
  }
};

// Fonction pour obtenir les statistiques de la boutique
export const getBoutiqueStats = async () => {
  try {
    const [produitsSnapshot, commandesSnapshot] = await Promise.all([
      getDocs(collection(db, 'boutique_produits')),
      getDocs(collection(db, 'boutique_commandes'))
    ]);
    
    const totalProduits = produitsSnapshot.size;
    const totalCommandes = commandesSnapshot.size;
    
    const produits = produitsSnapshot.docs.map(doc => doc.data());
    const commandes = commandesSnapshot.docs.map(doc => doc.data());
    
    const chiffreAffaires = commandes
      .filter(c => c.statut !== 'annule')
      .reduce((total, c) => total + (c.total || 0), 0);
    
    const stockTotal = produits.reduce((total, p) => total + (p.stock || 0), 0);
    
    return {
      totalProduits,
      totalCommandes,
      chiffreAffaires,
      stockTotal,
      produitsActifs: produits.filter(p => p.actif).length,
      commandesEnAttente: commandes.filter(c => c.statut === 'en_attente').length
    };
    
  } catch (error) {
    logger.error('Erreur stats boutique:', error);
    return null;
  }
};

export default { 
  initializeBoutique, 
  checkIfBoutiqueInitialized, 
  addDemoProduct, 
  getBoutiqueStats,
  produitsDemo 
}; 